import { Route } from '@angular/router';
import { LayoutComponent } from 'app/layout/layout.component';
import { MicrosoftRedirectErrorComponent } from './modules/admin/integrations/microsoft/microsoft-redirect-error/microsoft-redirect-error.component';
import { adminGuard } from './shared/guards/admin.guard';
import { licenseTypeGuard } from './shared/guards/license-type.guard';
import { loginProviderGuard } from './shared/guards/login-provider.guard';
import { microsoftConsentGuard } from './shared/guards/microsoft-consent.guard';
import { redirectBrowserExtensionToTenant } from './shared/guards/redirect-browser-extension-to-tenant.guard';
import { redirectLoggedInToPrompts } from './shared/guards/redirect-logged-in-to-prompts.guard';
import { redirectUnauthorizedToSignIn } from './shared/guards/redirect-unauthorized-to-sign-in.guard';
import { rightsGuard } from './shared/guards/rights.guard';
import { LicenseType } from './shared/models/public-payment-info';
import {
    CAN_ACCESS_ACCOUNT_MANAGEMENT,
    CAN_ACCESS_INTEGRATIONS,
    CAN_ACCESS_PROMPT_TEMPLATES,
    CAN_ACCESS_PUBLISH_PROMPTS,
    CAN_ACCESS_USER_MANAGEMENT,
} from './shared/models/rights';
import { TenantAccessService } from './shared/services/tenant-access.service';

export const appRoutes: Route[] = [
    {
        path: 'public-organization/:tenant',
        component: LayoutComponent,
        data: { layout: 'empty' },
        loadChildren: () => import('app/modules/public/public-organization-view/public-organization-view.routes'),
    },
    {
        path: 'public-prompt/:tenant/:promptId',
        component: LayoutComponent,
        data: { layout: 'empty' },
        loadChildren: () => import('app/modules/public/public-prompt-view/public-prompt-view.routes'),
    },
    {
        path: 'no-tenant',
        component: LayoutComponent,
        data: { layout: 'empty' },
        loadChildren: () => import('app/modules/auth/no-tenant/no-tenant.routes'),
    },
    {
        path: 'forgot-tenant',
        component: LayoutComponent,
        data: { layout: 'empty' },
        loadChildren: () => import('app/modules/auth/forgot-tenant/forgot-tenant.routes'),
    },
    {
        path: 'wrong-tenant',
        component: LayoutComponent,
        data: { layout: 'empty' },
        loadChildren: () => import('app/modules/auth/wrong-tenant/wrong-tenant.routes'),
    },
    {
        path: 'sign-up',
        component: LayoutComponent,
        data: { layout: 'empty' },
        loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes'),
    },

    {
        path: 'microsoft-consent-response',
        component: LayoutComponent,
        data: { layout: 'empty' },
        children: [
            {
                path: '',
                canActivate: [microsoftConsentGuard],
                component: MicrosoftRedirectErrorComponent,
            },
        ],
    },

    {
        path: 'browser-extensions/sidebar',
        canActivate: [redirectBrowserExtensionToTenant],
        component: LayoutComponent,
        pathMatch: 'full',
    },

    // Tenant related routes
    {
        path: ':tenant',
        resolve: {
            tenant: TenantAccessService,
        },
        canActivate: [loginProviderGuard],
        children: [
            {
                path: 'acceptinvite',
                canActivate: [redirectLoggedInToPrompts],
                component: LayoutComponent,
                data: { layout: 'empty' },
                loadChildren: () => import('app/modules/auth/accept-invite/accept-invite.routes'),
            },
            {
                path: 'sign-in',
                canActivate: [redirectLoggedInToPrompts],
                component: LayoutComponent,
                data: { layout: 'empty' },
                loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes'),
            },
            {
                path: 'forgot-password',
                component: LayoutComponent,
                canActivate: [redirectLoggedInToPrompts],
                data: { layout: 'empty' },
                loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes'),
            },
            {
                path: 'reset-password',
                component: LayoutComponent,
                canActivate: [redirectLoggedInToPrompts],
                data: { layout: 'empty' },
                loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes'),
            },
            {
                path: 'sign-out',
                canActivate: [redirectUnauthorizedToSignIn],
                component: LayoutComponent,
                data: { layout: 'empty' },
                loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes'),
            },
            {
                path: 'user-profile',
                canActivate: [redirectUnauthorizedToSignIn],
                component: LayoutComponent,
                data: { layout: 'logged-in-single' },
                loadChildren: () => import('app/modules/user-profile/user-profile.routes'),
            },
            {
                path: 'prompts',
                canActivate: [redirectUnauthorizedToSignIn],
                component: LayoutComponent,
                data: { layout: 'logged-in-main' },
                loadChildren: () => import('app/modules/admin/prompts/prompts.routes'),
            },
            {
                path: 'teams',
                canActivate: [
                    redirectUnauthorizedToSignIn,
                    licenseTypeGuard([LicenseType.PRO, LicenseType.ENTERPRISE]),
                ],
                component: LayoutComponent,
                data: { layout: 'logged-in-main' },
                loadChildren: () => import('app/modules/admin/teams/teams.routes'),
            },
            {
                path: 'plugins',
                canActivate: [redirectUnauthorizedToSignIn],
                component: LayoutComponent,
                data: { layout: 'logged-in-main' },
                loadChildren: () => import('app/modules/admin/plugins/plugins.routes'),
            },
            {
                path: 'admin',
                canActivate: [adminGuard],
                component: LayoutComponent,
                data: { layout: 'logged-in-main' },
                children: [
                    {
                        path: 'dashboard',
                        loadChildren: () => import('app/modules/admin/dashboard/dashboard.routes'),
                    },
                    {
                        path: 'publish-prompts',
                        canActivate: [
                            licenseTypeGuard([LicenseType.PRO, LicenseType.ENTERPRISE]),
                            rightsGuard(CAN_ACCESS_PUBLISH_PROMPTS),
                        ],
                        loadChildren: () => import('app/modules/admin/publish-prompts/publish-prompts.routes'),
                    },
                    {
                        path: 'user-management',
                        canActivate: [rightsGuard(CAN_ACCESS_USER_MANAGEMENT)],
                        loadChildren: () => import('app/modules/admin/user-management/user-management.routes'),
                    },
                    {
                        path: 'user-invites',
                        canActivate: [rightsGuard(CAN_ACCESS_USER_MANAGEMENT)],
                        loadChildren: () => import('app/modules/admin/user-invites/user-invites.routes'),
                    },
                    {
                        path: 'integrations',
                        canActivate: [licenseTypeGuard([LicenseType.ENTERPRISE]), rightsGuard(CAN_ACCESS_INTEGRATIONS)],
                        loadChildren: () => import('app/modules/admin/integrations/integrations.routes'),
                    },
                    {
                        path: 'account-management',
                        canActivate: [rightsGuard(CAN_ACCESS_ACCOUNT_MANAGEMENT)],
                        loadChildren: () => import('app/modules/admin/account-management/account-management.routes'),
                    },
                    {
                        path: 'prompt-templates',
                        canActivate: [rightsGuard(CAN_ACCESS_PROMPT_TEMPLATES)],
                        loadChildren: () => import('app/modules/admin/prompt-templates/prompt-templates.routes'),
                    },
                    {
                        path: '',
                        redirectTo: 'dashboard',
                        pathMatch: 'full',
                    },
                ],
            },

            {
                path: 'browser-extensions',
                canActivate: [redirectUnauthorizedToSignIn],
                component: LayoutComponent,
                data: { layout: 'logged-in-main' },
                loadChildren: () => import('app/modules/browser-extensions/browser-extensions.routes'),
            },

            // Default route for tenant
            {
                path: '',
                redirectTo: 'prompts',
                pathMatch: 'full',
            },
            {
                path: '**',
                redirectTo: 'prompts',
                pathMatch: 'full',
            },
        ],
    },

    // TODO: redirect to no-tenant page
    { path: '', redirectTo: 'no-tenant', pathMatch: 'full' },
    { path: '**', redirectTo: 'no-tenant', pathMatch: 'full' },
];
